import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CreativeWithExternalId } from '../../services/measurementService';
import {
  isAllowedCreativeFile,
  UNSUPPORTED_CREATIVE_MESSAGE,
} from '../../utils/creativeFileValidation';
import InfoTooltip from '../InfoTooltip';
import './AddCreativeDialog.css';

type SourceMode = 'file' | 'url';

interface AddCreativeDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (creative: CreativeWithExternalId) => void | Promise<void>;
  disabled?: boolean;
}

function isHttpUrl(value: string): boolean {
  try {
    const uri = new URL(value);
    return uri.protocol === 'http:' || uri.protocol === 'https:';
  } catch {
    return false;
  }
}

const AddCreativeDialog: React.FC<AddCreativeDialogProps> = ({
  open,
  onClose,
  onAdd,
  disabled = false,
}) => {
  const [sourceMode, setSourceMode] = useState<SourceMode>('file');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [externalId, setExternalId] = useState('');
  const [error, setError] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isAdding) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isAdding, onClose]);

  if (!open) {
    return null;
  }

  const resetFields = () => {
    setFile(null);
    setPreviewUrl('');
    setExternalId('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || isAdding) {
      return;
    }

    const id = externalId.trim();
    if (!id) {
      setError('Please enter an external ID');
      return;
    }

    let creative: CreativeWithExternalId;
    if (sourceMode === 'file') {
      if (!file) {
        setError('Please choose a file');
        return;
      }
      if (!isAllowedCreativeFile(file)) {
        setError(UNSUPPORTED_CREATIVE_MESSAGE);
        return;
      }
      creative = { file, externalId: id };
    } else {
      const url = previewUrl.trim();
      if (!url) {
        setError('Please enter a preview URL');
        return;
      }
      if (!isHttpUrl(url)) {
        setError('Preview URLs must be http or https');
        return;
      }
      creative = { previewUrl: url, externalId: id };
    }

    setIsAdding(true);
    setError('');
    try {
      await onAdd(creative);
      resetFields();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add creative');
    } finally {
      setIsAdding(false);
    }
  };

  const busy = disabled || isAdding;

  return createPortal(
    <div
      className="add-creative-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <div
        className="add-creative-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-creative-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="add-creative-dialog-title">Add creative</h2>
        <form onSubmit={handleAdd}>
          <div className="form-group">
            <span className="add-creative-label">Creative</span>
            <div className="add-creative-source-toggle" role="group" aria-label="Creative source">
              <button
                type="button"
                className={sourceMode === 'file' ? 'is-active' : ''}
                aria-pressed={sourceMode === 'file'}
                onClick={() => {
                  setSourceMode('file');
                  setError('');
                }}
                disabled={busy}
              >
                File
              </button>
              <button
                type="button"
                className={sourceMode === 'url' ? 'is-active' : ''}
                aria-pressed={sourceMode === 'url'}
                onClick={() => {
                  setSourceMode('url');
                  setError('');
                }}
                disabled={busy}
              >
                URL
              </button>
            </div>
            <div className="add-creative-source-slot">
              {sourceMode === 'file' ? (
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,.zip,application/zip"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    setError('');
                  }}
                  disabled={busy}
                />
              ) : (
                <input
                  type="url"
                  value={previewUrl}
                  onChange={(e) => {
                    setPreviewUrl(e.target.value);
                    setError('');
                  }}
                  placeholder="https://"
                  autoComplete="off"
                  disabled={busy}
                />
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="add-creative-external-id" className="add-creative-external-id-label">
              External ID <span className="required">*</span>
              <InfoTooltip text="The ID of this creative in the DSP serving the ads. Use the same value the ad server inserts into the tracking tag's creative ID macro (cid), so impressions and reports can be matched to this creative." />
            </label>
            <input
              id="add-creative-external-id"
              type="text"
              value={externalId}
              onChange={(e) => {
                setExternalId(e.target.value);
                setError('');
              }}
              placeholder="Enter external ID"
              disabled={busy}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="add-creative-dialog-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={busy}>
              Done
            </button>
            <button type="submit" className="create-button" disabled={busy}>
              {isAdding ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddCreativeDialog;
